import React from 'react';
import { Lock, Crown, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../services/auth';
import { useSubscriptionStore } from '../../stores/subscriptionStore';

interface ProFeatureGuardProps {
  children: React.ReactNode;
  feature: string;
}

export const ProFeatureGuard: React.FC<ProFeatureGuardProps> = ({ children, feature }) => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const setShowAuthModal = useAuthStore(state => state.setShowAuthModal);
  const hasProAccess = useSubscriptionStore(state => state.checkProAccess());

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Lock className="w-12 h-12 text-gold-500 mb-4" />
        <h2 className="text-xl font-semibold text-gold-500 mb-2">Sign in Required</h2>
        <p className="text-white/60 mb-4">
          Please sign in to access {feature}
        </p>
        <button
          onClick={() => setShowAuthModal(true)}
          className="px-6 py-2 bg-gold-500 text-gray-900 rounded-lg hover:bg-gold-400 transition-colors"
        >
          Sign In
        </button>
      </div>
    );
  }

  if (!hasProAccess) {
    return (
      <div className="flex flex-col items-center justify-center p-8 max-w-2xl mx-auto text-center">
        <Crown className="w-12 h-12 text-gold-500 mb-4" />
        <h2 className="text-2xl font-semibold text-gold-500 mb-4">Upgrade to Pro</h2>
        <p className="text-white/80 text-lg mb-6">
          {feature} is a premium feature designed to enhance your Bible study experience
        </p>
        
        <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 mb-8 w-full">
          <h3 className="text-xl font-semibold text-gold-400 mb-4">Pro Features Include:</h3>
          <ul className="space-y-3 text-left">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
              <span className="text-white/80">AI-powered insights and verse analysis</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
              <span className="text-white/80">Personalized scripture recommendations based on your mood and situation</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
              <span className="text-white/80">Unlimited access to all premium features</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
              <span className="text-white/80">Priority support and future feature updates</span>
            </li>
          </ul>
        </div>

        <button
          onClick={() => navigate('/preferences')}
          className="px-8 py-3 bg-gold-500 text-gray-900 rounded-lg hover:bg-gold-400 transition-colors font-semibold"
        >
          Upgrade Now
        </button>
        <p className="text-white/60 mt-4 text-sm">
          Starting at just $4.99/month
        </p>
      </div>
    );
  }

  return <>{children}</>;
};
